import { forwardRef, useEffect, useState, useRef } from 'react';

interface CustomInputProps {
  value?: string;
  onChange?: (value: string, postalCode?: string) => void;
  placeholder?: string;
  [key: string]: any;
  id?: string;
}

interface LoqateAddress {
  Line1: string;
  Line2: string;
  City: string;
  Province: string;
  PostalCode: string;
  CountryName: string;
}

declare global {
  interface Window {
    pca: {
      Address: new (fields: any[], options: any) => {
        listen: (event: string, callback: (address: LoqateAddress) => void) => void;
      };
      fieldMode: {
        POPULATE: number;
        COUNTRY: number;
      };
    };
  }
}

// Keep track of script loading state
let scriptLoadingPromise: Promise<void> | null = null;

const loadLoqateScript = () => {
  if (scriptLoadingPromise) return scriptLoadingPromise;

  scriptLoadingPromise = new Promise((resolve) => {
    // Add Loqate CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.type = 'text/css';
    link.href = 'https://api.addressy.com/css/address-3.99.min.css';
    document.head.appendChild(link);

    // Add custom CSS for dropdown styling
    const style = document.createElement('style');
    style.textContent = `
      .address-search-container {
        position: relative;
      }
      .address-search-container .pca-container {
        width: 100% !important;
        background: white !important;
        border: 1px solid #ccc !important;
        border-radius: 4px !important;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
        z-index: 9999 !important;
      }
    `;
    document.head.appendChild(style);

    // Initialize Loqate
    const script = document.createElement('script');
    script.src = 'https://api.addressy.com/js/address-3.99.min.js';
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      resolve();
    };
  });

  return scriptLoadingPromise;
};

// Unique ID generator
const getUniqueId = (() => {
  let count = 0;
  return (prefix: string) => `${prefix}-${++count}`;
})();

const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>((props, ref) => {
  const { value, onChange, id: propId, ...rest } = props;
  const [searchTerm, setSearchTerm] = useState(value || '');
  const containerRef = useRef<HTMLDivElement>(null);

  // Update searchTerm when value prop changes
  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  // Generate unique IDs for this instance
  const [uniqueId] = useState(() => propId || getUniqueId('address'));
  const searchId = `${uniqueId}-search`;
  const line1Id = `${uniqueId}-line1`;
  const line2Id = `${uniqueId}-line2`;
  const cityId = `${uniqueId}-city`;
  const stateId = `${uniqueId}-state`;
  const postcodeId = `${uniqueId}-postcode`;
  const countryId = `${uniqueId}-country`;

  useEffect(() => {
    loadLoqateScript().then(() => {
      if (!containerRef.current) return;

      // Remove any existing dropdowns in this container
      const existingDropdowns = containerRef.current.querySelectorAll('.pca-container');
      existingDropdowns.forEach(dropdown => dropdown.remove());

      // Prevent double initialization
      if ((containerRef.current as any).__loqateInitialized) return;
      (containerRef.current as any).__loqateInitialized = true;

      const fields = [
        { element: searchId, field: "" },
        { element: line1Id, field: "Line1" },
        { element: line2Id, field: "Line2", mode: window.pca.fieldMode.POPULATE },
        { element: cityId, field: "City", mode: window.pca.fieldMode.POPULATE },
        { element: stateId, field: "Province", mode: window.pca.fieldMode.POPULATE },
        { element: postcodeId, field: "PostalCode", mode: window.pca.fieldMode.POPULATE },
        { element: countryId, field: "CountryName", mode: window.pca.fieldMode.COUNTRY }
      ];

      const options = {
        key: "BD38-WM47-TE66-MK89",
        countries: ["GB"],
        country: "GB",
        filter: "GB",
        searchType: "ADDRESS",
        language: "en-GB",
        debounce: 100,
        limit: 10,
        simulateReactEvents: true,
        container: containerRef.current,
        showResults: true,
        showResultsOnFocus: true
      };

      const control = new window.pca.Address(fields, options);

      control.listen("populate", (address: LoqateAddress) => {
        if (onChange) {
          const formattedAddress = [
            address.Line1,
            address.Line2,
            address.City,
            address.Province,
            address.PostalCode,
            address.CountryName
          ].filter(Boolean).join(', ');
          setSearchTerm(formattedAddress);
          onChange(formattedAddress, address.PostalCode);
        }
        // Hide the dropdown after selection
        const container = containerRef.current?.querySelector('.pca-container') as HTMLElement;
        if (container) {
          container.style.display = 'none';
        }
        // Blur the input to prevent Loqate from re-triggering the dropdown
        const input = containerRef.current?.querySelector('input') as HTMLInputElement;
        if (input) {
          input.blur();
        }
        // Fallback: Hide the dropdown again after a short delay
        setTimeout(() => {
          const container = containerRef.current?.querySelector('.pca-container') as HTMLElement;
          if (container) {
            container.style.display = 'none';
          }
        }, 200);
      });
    });
  }, [onChange, searchId, line1Id, line2Id, cityId, stateId, postcodeId, countryId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (onChange) {
      onChange(e.target.value);
    }
  };

  return (
    <div className="address-search-container" ref={containerRef} style={{ position: 'relative' }}>
      <input
        ref={ref}
        id={searchId}
        placeholder={'Start typing your address or postcode * '}
        {...rest}
        value={searchTerm}
        onChange={handleChange}
      />
      <input type="hidden" id={line1Id} className='line-1' />
      <input type="hidden" id={line2Id} className='line-2' />
      <input type="hidden" id={cityId} className='city' />
      <input type="hidden" id={stateId} className='state' />
      <input type="hidden" id={postcodeId} className='postcode' />
      <input type="hidden" id={countryId} className='country' />
    </div>
  );
});

CustomInput.displayName = 'CustomInput';

export type { CustomInputProps };
export { CustomInput }; 